// Global Instructions Rule Applied!
//
// The Capital call notices section of a commitment: which additional notice
// documents (the ILPA workbook) go out with its capital call notices.
//   Use the investor's setting — the documents set on the investor (the
//     default; shown beside the option, "None" when the investor has none)
//   Set for this commitment — the commitment's own set, which may be none
// Shared by the Commitment form, the Consolidated commitment form (where the
// setting applies to the commitment in every member fund) and the New
// commitment form. A pocket of a consolidated commitment shows what it
// carries, read-only: it is set on the consolidated commitment.

import React, { useMemo } from 'react'
import { Radio } from 'antd'

import { FormSection } from '../../../core/components/FormSection.tsx'
import HelpTip from '../../../core/components/HelpTip.tsx'
import { InputManager } from '../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type FieldConfig, type FieldValue } from '../../../core/components/InputManager/types.ts'
import type { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import { CommitmentFieldKeys, toIdList, type CommitmentNoticeDraft } from '../utils/commitmentDrafts.ts'
import { getNoticeDocumentFields } from '../utils/commitmentFieldConfigs.ts'

export const NOTICE_DOCUMENTS_HELP =
  'Sent with every capital call notice for this commitment, in addition to the notice itself. ' +
  "The investor's setting is changed on the Investor form."
export const NOTICE_CONSOLIDATED_HELP = `${NOTICE_DOCUMENTS_HELP} Applies to the commitment in both funds.`
export const NOTICE_POCKET_REASON = 'Set on the consolidated commitment'

interface Props {
  notice: CommitmentNoticeDraft
  /** the documents the investor's notices carry by default */
  investorTemplateIds: number[]
  /** cfg.notice_templates supplements */
  options: SelectListEntry[]
  onChange: (next: CommitmentNoticeDraft) => void
  help?: string
  /** set = read-only, with the reason behind the "?" */
  readOnlyReason?: string | null
  /** bumped by the form to put the fields back (Cancel) */
  resetVersion?: number
}

const labelsOf = (ids: number[], options: SelectListEntry[]): string => {
  const labels = ids
    .map((id) => options.find((option) => option.value === id)?.label)
    .filter((label): label is string => Boolean(label))
  return labels.length > 0 ? labels.join(', ') : 'None'
}

export function CommitmentNoticeSection({
  notice,
  investorTemplateIds,
  options,
  onChange,
  help = NOTICE_DOCUMENTS_HELP,
  readOnlyReason = null,
  resetVersion = 0
}: Props): React.ReactElement {
  const investorLabels = useMemo(() => labelsOf(investorTemplateIds, options), [investorTemplateIds, options])

  const readFields = useMemo<FieldConfig[]>(
    () => [
      {
        key: CommitmentFieldKeys.notice_template_ids,
        label: 'Additional notice documents',
        type: FieldTypeEnum.TEXT,
        value: labelsOf(notice.overridden ? notice.template_ids : investorTemplateIds, options),
        editable: false,
        labelTooltip: readOnlyReason ?? undefined
      }
    ],
    [notice, investorTemplateIds, options, readOnlyReason]
  )
  const documentFields = useMemo(
    () => getNoticeDocumentFields(notice, options, !notice.overridden),
    [notice, options]
  )

  if (readOnlyReason) {
    return (
      <FormSection title='Capital call notices' titleColor='purple' scrollable={false}>
        <InputManager inputMode='read' labelPosition='above' fields={readFields} />
      </FormSection>
    )
  }

  return (
    <FormSection title='Capital call notices' titleColor='purple' scrollable={false}>
      <div className='text-sm font-medium text-gray-700 mb-1'>
        Additional notice documents
        <HelpTip title={help} />
      </div>
      <Radio.Group
        className='flex flex-col gap-1'
        value={notice.overridden ? 'own' : 'investor'}
        onChange={(e) => onChange({ ...notice, overridden: e.target.value === 'own' })}
      >
        <Radio value='investor'>
          Use the investor&apos;s setting <span className='text-gray-500'>({investorLabels})</span>
        </Radio>
        <Radio value='own'>Set for this commitment</Radio>
      </Radio.Group>
      {notice.overridden ? (
        <div className='mt-2 max-w-xl'>
          <InputManager
            key={`noticeFields-${resetVersion}`}
            inputMode='edit'
            labelPosition='none'
            fields={documentFields}
            onChange={(_key: string, value: FieldValue) => onChange({ ...notice, template_ids: toIdList(value) })}
          />
        </div>
      ) : null}
    </FormSection>
  )
}

export default CommitmentNoticeSection
